/**
 * The root layout's theme-class composition, ported verbatim (`app/layout.tsx`
 * lines 352–389). Pure — it is arithmetic over two strings — so it moves across
 * unchanged; only its INPUTS were server-resolved (see `shell-data.ts`).
 *
 * Two independent settings compose the theme:
 *   • appearance.theme  = background mode: "light" | "dark" | "system"
 *   • appearance.accent = accent name (indigo…rose)
 *
 * The <html> class is the pair `${accent}-${background}` PLUS the bare
 * `${background}` — e.g. "indigo-dark dark". Both are load-bearing and neither
 * is decoration: globals.css keys dark/light off the BARE class (the `.dark {…}`
 * block and the `dark:` variant), while the ACCENT class carries the generated
 * `--primary`/`--ring` override. Emitting one without the other yields a page
 * that is the right colour and the wrong theme, or vice versa.
 *
 * "system" has no server signal, so it resolves via the `theme` cookie (a
 * background hint the client writes) and then falls back to dark. Legacy
 * composite values ("duotone-dark") and unknown accents both degrade to a valid
 * pair — background from the suffix, accent to brand indigo.
 */

import { normalizeWebAccent } from "@excellent/ui/theme-accents";

export type ThemeBackground = "light" | "dark";

/** Background implied by a raw setting/cookie value, or null when it says nothing. */
function backgroundFrom(v: string | undefined): ThemeBackground | null {
  if (!v) return null;
  if (v.endsWith("-light") || v === "light") return "light";
  if (v.endsWith("-dark") || v === "dark") return "dark";
  return null; // "system" or unrecognised
}

export interface ResolvedTheme {
  /** `${accent}-${background}` — carries the accent custom properties. */
  themeClass: string;
  /** The bare `light` | `dark` — what globals.css keys its dark block off. */
  background: ThemeBackground;
  /** Whether the DB carries an explicit choice (ThemeProvider self-heals if not). */
  dbHasExplicitTheme: boolean;
}

export function resolveTheme(dbTheme: string, accentSetting: string, themeCookie?: string): ResolvedTheme {
  const dbHasExplicitTheme =
    dbTheme === "light" || dbTheme === "dark" || dbTheme === "system" || dbTheme.includes("-");
  const background = backgroundFrom(dbTheme) ?? backgroundFrom(themeCookie) ?? "dark";
  const accent = normalizeWebAccent(accentSetting);
  return { themeClass: `${accent}-${background}`, background, dbHasExplicitTheme };
}

/**
 * Apply the theme to <html>.
 *
 * In the Next app the classes were RENDERED into the `<html>` tag server-side,
 * which is what avoids a flash of the wrong theme. An SPA has no server render,
 * so the equivalent guarantee comes from `client/index.html`: it ships with the
 * dark default already on `<html>`, and this only reconciles once the real
 * settings arrive. Never let this be the FIRST thing that sets a theme class, or
 * the app flashes light-on-dark on every cold load.
 */
export function applyTheme(root: HTMLElement, theme: ResolvedTheme): void {
  const keep = Array.from(root.classList).filter(
    (c) => !c.endsWith("-light") && !c.endsWith("-dark") && c !== "light" && c !== "dark",
  );
  root.className = [...keep, theme.themeClass, theme.background].join(" ");
}
